class PdpComplementaryProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this._drawer = this.querySelector('x-drawer');
    this._addButton = this._drawer?.querySelector('[data-add-selected]');
    this._hint = this._drawer?.querySelector('[data-complementary-hint]');

    this.#bindDrawerTriggers();

    this._drawer?.querySelectorAll('[data-complementary-item]').forEach((item) => this.#initItem(item));

    this._addButton?.addEventListener('click', () => this.#addToCart());

    this.#updateAddButton();
    this.#initRailBounds();
  }

  disconnectedCallback() {
    if (this._openDrawerHandler) {
      this.querySelectorAll('[data-open-drawer]').forEach((button) => {
        button.removeEventListener('click', this._openDrawerHandler);
      });

      this.closest('.shopify-section--main-product')
        ?.querySelectorAll('[data-open-complementary-drawer]')
        .forEach((button) => {
          button.removeEventListener('click', this._openDrawerHandler);
        });
    }

    if (this._syncRailBounds) {
      window.removeEventListener('scroll', this._syncRailBounds);
      window.removeEventListener('resize', this._syncRailBounds);
    }

    if (this._invalidateRailBottomOffset) {
      window.removeEventListener('resize', this._invalidateRailBottomOffset);
    }

    this._railMediaQuery?.removeEventListener('change', this._syncRailBounds);
    this._railResizeObserver?.disconnect();
  }

  #initRailBounds() {
    this._rail = this.querySelector('.hp-pdp-complementary__rail');

    if (!this._rail) {
      return;
    }

    this._railSection = this.closest('.shopify-section--main-product');
    this._railBoundary = this._railSection?.querySelector('.product');

    if (!this._railSection || !this._railBoundary) {
      return;
    }

    this._syncRailBounds = this.#syncRailBounds.bind(this);
    this._railMediaQuery = window.matchMedia('(min-width: 990px)');

    window.addEventListener('scroll', this._syncRailBounds, { passive: true });
    window.addEventListener('resize', this._syncRailBounds);
    this._railMediaQuery.addEventListener('change', this._syncRailBounds);

    this._invalidateRailBottomOffset = () => {
      this._railBottomOffset = null;
    };

    window.addEventListener('resize', this._invalidateRailBottomOffset);

    if (typeof ResizeObserver !== 'undefined') {
      this._railResizeObserver = new ResizeObserver(this._syncRailBounds);
      this._railResizeObserver.observe(this._rail);
      this._railResizeObserver.observe(this._railBoundary);
      this._railResizeObserver.observe(this._railSection);
    }

    this.#syncRailBounds();
  }

  #bindDrawerTriggers() {
    this._openDrawerHandler =
      this._openDrawerHandler ??
      (() => {
        this._drawer?.show();
        window.requestAnimationFrame(() => this.#updateAddButton());
      });

    this.querySelectorAll('[data-open-drawer]').forEach((button) => {
      button.addEventListener('click', this._openDrawerHandler);
    });

    this.closest('.shopify-section--main-product')
      ?.querySelectorAll('[data-open-complementary-drawer]')
      .forEach((button) => {
        button.addEventListener('click', this._openDrawerHandler);
      });
  }

  #readRailBottomOffset() {
    if (this._railBottomOffset != null) {
      return this._railBottomOffset;
    }

    const savedTop = this._rail.style.top;
    const savedBottom = this._rail.style.bottom;

    this._rail.style.removeProperty('top');
    this._rail.style.removeProperty('bottom');
    this._railBottomOffset = Number.parseFloat(getComputedStyle(this._rail).bottom) || 110;

    if (savedTop) {
      this._rail.style.top = savedTop;
    }

    if (savedBottom) {
      this._rail.style.bottom = savedBottom;
    }

    return this._railBottomOffset;
  }

  #syncRailBounds() {
    if (!this._rail || !this._railBoundary || !this._railSection) {
      return;
    }

    if (!this._railMediaQuery?.matches) {
      this._rail.classList.remove('is-outside-section');
      this._rail.style.removeProperty('top');
      this._rail.style.removeProperty('bottom');
      this._railBottomOffset = null;
      return;
    }

    const viewportHeight = window.innerHeight;
    const boundaryRect = this._railBoundary.getBoundingClientRect();
    const railHeight = this._rail.offsetHeight;
    const maxTop = boundaryRect.bottom - railHeight;
    const minTop = boundaryRect.top;

    if (boundaryRect.bottom <= 0 || boundaryRect.top >= viewportHeight || maxTop < minTop) {
      this._rail.classList.add('is-outside-section');
      return;
    }

    const bottomOffset = this.#readRailBottomOffset();
    const naturalTop = viewportHeight - bottomOffset - railHeight;

    this._rail.classList.remove('is-outside-section');

    if (naturalTop > maxTop) {
      this._rail.style.bottom = 'auto';
      this._rail.style.top = `${maxTop}px`;
      return;
    }

    this._rail.style.removeProperty('top');
    this._rail.style.removeProperty('bottom');
  }

  #initItem(item) {
    const variantsScript = item.querySelector('[data-product-variants]');

    item._variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
    item._sizePosition = parseInt(item.dataset.sizePosition || '0', 10);

    if (item.dataset.hasSize !== 'true') {
      item.querySelector('[data-select-product]')?.addEventListener('click', (event) => {
        if (event.currentTarget.disabled) return;
        this.#toggleNoSizeItem(item);
      });

      return;
    }

    item.querySelectorAll('[data-size-value]').forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled || button.classList.contains('is-disabled')) {
          return;
        }

        const isAlreadySelected = button.classList.contains('is-selected');

        if (isAlreadySelected) {
          this.#deselectItem(item);
          this.#updateAddButton();
          return;
        }

        item.querySelectorAll('[data-size-value]').forEach((sizeButton) => {
          sizeButton.classList.remove('is-selected');
          sizeButton.removeAttribute('aria-pressed');
        });

        button.classList.add('is-selected');
        button.setAttribute('aria-pressed', 'true');

        const sizeValue = button.getAttribute('data-size-value');
        const variant = item._variants.find(
          (entry) => entry.available && entry.options[item._sizePosition - 1] === sizeValue
        );

        if (variant) {
          item.dataset.selectedVariantId = String(variant.id);
          item.classList.add('is-selected');
        } else {
          this.#deselectItem(item);
        }

        this.#updateAddButton();
      });
    });
  }

  #deselectItem(item) {
    item.querySelectorAll('[data-size-value].is-selected').forEach((sizeButton) => {
      sizeButton.classList.remove('is-selected');
      sizeButton.removeAttribute('aria-pressed');
    });
    delete item.dataset.selectedVariantId;
    item.classList.remove('is-selected');
  }

  #toggleNoSizeItem(item) {
    if (item.dataset.selectedVariantId) {
      delete item.dataset.selectedVariantId;
      item.classList.remove('is-selected');
    } else {
      const defaultVariantId = item.dataset.defaultVariantId;

      if (defaultVariantId) {
        item.dataset.selectedVariantId = defaultVariantId;
        item.classList.add('is-selected');
      }
    }

    const selectButton = item.querySelector('[data-select-product]');
    selectButton?.setAttribute('aria-pressed', String(Boolean(item.dataset.selectedVariantId)));
    selectButton?.classList.toggle('is-selected', Boolean(item.dataset.selectedVariantId));
    this.#updateAddButton();
  }

  #getSelectedItems() {
    return Array.from(this._drawer?.querySelectorAll('[data-complementary-item]') ?? []).filter((item) =>
      this.#itemIsReady(item)
    );
  }

  #isValid() {
    return this.#getSelectedItems().length > 0;
  }

  #itemIsReady(item) {
    const variantId = parseInt(item.dataset.selectedVariantId || '', 10);

    if (!variantId) {
      return false;
    }

    const variant = item._variants.find((entry) => entry.id === variantId);
    return Boolean(variant?.available);
  }

  #updateAddButton() {
    this._addButton = this._drawer?.querySelector('[data-add-selected]') ?? this._addButton;

    if (!this._addButton) {
      return;
    }

    const isValid = this.#isValid();
    this._addButton.disabled = !isValid;

    if (this._hint) {
      this._hint.hidden = true;
    }
  }

  async #addToCart() {
    if (!this.#isValid() || !this._addButton) {
      return;
    }

    const items = this.#getSelectedItems().map((item) => ({
      id: parseInt(item.dataset.selectedVariantId, 10),
      quantity: 1
    }));

    this._addButton.setAttribute('aria-busy', 'true');
    this._addButton.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      const responseJson = await response.json();
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this._addButton.removeAttribute('aria-busy');
      this.#updateAddButton();

      if (response.ok) {
        this._drawer?.hide();

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              onSuccessDo: 'force_open_drawer',
              cart: cartContent
            }
          })
        );
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (_error) {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      this._addButton.removeAttribute('aria-busy');
      this.#updateAddButton();
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }
}

if (!window.customElements.get('pdp-complementary-products')) {
  window.customElements.define('pdp-complementary-products', PdpComplementaryProducts);
}
